import { create } from 'zustand';
import type {
  BusinessInfoData,
  OwnerInfoData,
  AddressLocationData,
  ServiceOptionsData
} from '../services/onboardingService';

interface OnboardingState {
  // Form Data
  businessInfo: BusinessInfoData;
  ownerInfo: OwnerInfoData;
  addressLocation: AddressLocationData;
  serviceOptions: ServiceOptionsData;

  // Track if data is loaded from backend
  isLoadedFromBackend: boolean;

  // Actions
  setBusinessInfo: (data: BusinessInfoData) => void;
  setOwnerInfo: (data: OwnerInfoData) => void;
  setAddressLocation: (data: AddressLocationData) => void;
  setServiceOptions: (data: ServiceOptionsData) => void;
  clearOnboardingData: () => void;
  loadFromBackend: (agencyData: any) => void;
}

const initialBusinessInfo: BusinessInfoData = {
  businessName: '',
  displayName: '',
  numberOfCars: 1,
  description: '',
  patenteId: '',
};

const initialOwnerInfo: OwnerInfoData = {
  firstName: '',
  lastName: '',
  position: '',
};

const initialAddressLocation: AddressLocationData = {
  street: '',
  city: '',
  governorate: '',
  country: 'Tunisia',
  postal_code: '',
  coordinates: [0, 0],
};

const initialServiceOptions: ServiceOptionsData = {
  homeDelivery: false,
  airportPickup: false,
  longTermRental: false,
  instantBooking: false,
};

// Session-only store - data cleared on logout or tab close
export const useOnboardingStore = create<OnboardingState>((set) => ({
  // Initial state
  businessInfo: initialBusinessInfo,
  ownerInfo: initialOwnerInfo,
  addressLocation: initialAddressLocation,
  serviceOptions: initialServiceOptions,
  isLoadedFromBackend: false,

  // Actions
  setBusinessInfo: (data: BusinessInfoData) => set({ businessInfo: data }),

  setOwnerInfo: (data: OwnerInfoData) => set({ ownerInfo: data }),

  setAddressLocation: (data: AddressLocationData) => set({ addressLocation: data }),

  setServiceOptions: (data: ServiceOptionsData) => set({ serviceOptions: data }),

  clearOnboardingData: () => set({
    businessInfo: initialBusinessInfo,
    ownerInfo: initialOwnerInfo,
    addressLocation: initialAddressLocation,
    serviceOptions: initialServiceOptions,
    isLoadedFromBackend: false,
  }),

  loadFromBackend: (agencyData: any) => {
    const updates: Partial<OnboardingState> = {
      isLoadedFromBackend: true,
    };

    // Load business info
    if (agencyData.businessName || agencyData.displayName || agencyData.description) {
      updates.businessInfo = {
        businessName: agencyData.businessName || '',
        displayName: agencyData.displayName || '',
        numberOfCars: agencyData.numberOfCars || 1,
        description: agencyData.description || '',
        patenteId: agencyData.legalInfo?.patente?.number || '',
      };
    }

    // Load owner info
    if (agencyData.ownerInfo) {
      updates.ownerInfo = {
        firstName: agencyData.ownerInfo.firstName || '',
        lastName: agencyData.ownerInfo.lastName || '',
        position: agencyData.ownerInfo.position || 'Owner',
      };
    }

    // Load address and location
    if (agencyData.address || agencyData.location) {
      updates.addressLocation = {
        street: agencyData.address?.street || '',
        city: agencyData.address?.city || '',
        governorate: agencyData.address?.governorate || '',
        country: agencyData.address?.country || 'Tunisia',
        postal_code: agencyData.address?.postal_code || '',
        coordinates: agencyData.location?.coordinates || [0, 0],
      };
    }

    // Load service options
    if (agencyData.serviceOptions) {
      updates.serviceOptions = {
        homeDelivery: agencyData.serviceOptions.homeDelivery || false,
        airportPickup: agencyData.serviceOptions.airportPickup || false,
        longTermRental: agencyData.serviceOptions.longTermRental || false,
        instantBooking: agencyData.serviceOptions.instantBooking || false,
      };
    }

    set(updates);
  },
}));
